import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Accordion, Col, Dropdown, DropdownButton, Row } from "react-bootstrap";
import style from "./styles/ProviderSchedulePage.module.scss";
import { Schedule as ScheduleModel, ScheduleType} from "../../data/entities";
import { usePageActions } from "../PageLayout";
import { ScheduleBlock } from "../../data/entities";
import { formatDate, getTodayAtMidnight, parseLocalDate } from "../../utils";
import { useCRUDHandlers, useSchedule } from "./hooks";
import { useGridActions, useSharedState, useUrlParams } from "../../hooks";
import { AddScheduleModal, CloneScheduleModal, EditScheduleModal, SectionHeader } from "./components";
import { Card, ConfirmationModal, Icon, IconAndText, IconDefinitions } from "../../components";
import Schedule from "../../components/Schedule/Schedule";
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import { useDispatch } from "react-redux";
import { invalidateSchedules } from "../../app";

export function ProviderSchedulePage() {

  const dispatch = useDispatch(); 
  const providerId = useUrlParams("providerId");
  const { editItemState: scheduleEditItemState,  showEditState, showAddState, showDeleteState, deleteItemState, showCloneState } = useGridActions<ScheduleModel>()
  const { defaultSchedules, exceptionSchedules, refreshData } = useSchedule();
  const scheduleTypeState = useSharedState<ScheduleType>({} as ScheduleType);
  const scheduleCloneItemState = useSharedState<ScheduleModel>({} as ScheduleModel);
  
  const [activeAccordionKeys, setActiveAccordionKeys] = useState<string[]>([]); 
  const [targetAccordionKey, setTargetAccordionKey] = useState<string | undefined>(undefined); 
  const scrollTargetRef = useRef<string | null>(null); 
  
  const shouldAutoOpenSchedule = defaultSchedules && defaultSchedules.length === 1;
  const shouldAutoOpenExcepton = exceptionSchedules && exceptionSchedules.length === 1; 
  
  const userId = defaultSchedules?.[0]?.provider?.userId || exceptionSchedules?.[0]?.provider?.userId;

  const { clone, createSchedule, deleteSchedule, updateSchedule } = useCRUDHandlers(
    () => {
      refreshData();
      if (userId) 
        dispatch(invalidateSchedules(userId));
    }, 
    showAddState, 
    showCloneState,
    setTargetAccordionKey,
  );

  useLayoutEffect(() => {
    if(targetAccordionKey) {
      const scheduleExists = 
        defaultSchedules?.some(s => `default-${s.id}` === targetAccordionKey) ||
        exceptionSchedules?.some(s => `exception-${s.id}` === targetAccordionKey);
      
      if (scheduleExists) {
        setActiveAccordionKeys(prev => 
          prev.includes(targetAccordionKey) ? prev : [...prev, targetAccordionKey]
        );
        scrollTargetRef.current = targetAccordionKey;
        setTargetAccordionKey(undefined);
      }
    }
  }, [targetAccordionKey, defaultSchedules, exceptionSchedules])

  useEffect(() => {
    if (scrollTargetRef.current) {
      const element = document.getElementById(scrollTargetRef.current);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrollTargetRef.current = null; 
      }
    }
  }, [activeAccordionKeys]); 

  usePageActions(
    <DropdownButton id="dropdown-basic-button" title={<IconAndText iconName={IconDefinitions.add} text="Add"/>}>
      <Dropdown.Item className="text-center" onClick={() => {
        scheduleTypeState.setter(ScheduleType.Default);
        showAddState.setter(true)
      }}>Schedule</Dropdown.Item>
      <Dropdown.Item className="text-center" onClick={() => {
        scheduleTypeState.setter(ScheduleType.Override);
        showAddState.setter(true)
      }}>Exception</Dropdown.Item>
    </DropdownButton>
  );

  const handleAccordionToggle = (selectedEventKey: AccordionEventKey, accordionKey: string) => {
    setActiveAccordionKeys(prev => {
      const keyStr = typeof selectedEventKey === 'string' ? selectedEventKey : null;
      if (keyStr  === null || !!keyStr ) {
        return prev.filter(k => k !== accordionKey);
      } else {
        return prev.includes(accordionKey) ? prev : [...prev, accordionKey];
      }
    });
  };

  return (
    <>
      <Card title="Schedule">
        <Row>
          <Col lg={12}>
            <SectionHeader
              title="Default Schedules"
              description="Recurring weekly availability"
              iconName={IconDefinitions.calendar}
              iconBgColor="#577c8e"
              borderColor="#577c8e"
              count={defaultSchedules?.length || 0}
              countLabel="schedule"
            />
            <div className="d-flex flex-column justify-content-between">
              {defaultSchedules && defaultSchedules.map((schedule, index) => {
                const status = getScheduleStatus(schedule.effectiveDate);
                const accordionKey = `default-${schedule.id}`;
                
                return (
                  <Accordion 
                    id={accordionKey} 
                    // Bootstrap accordion prop that tells accordion.item which one should be open (eventKey)
                    // If undefined all the accordion.itmes are closed
                    defaultActiveKey={
                      activeAccordionKeys.includes(accordionKey) || (shouldAutoOpenSchedule && !activeAccordionKeys.length)
                        ? accordionKey 
                        : undefined
                    }
                    key={`${accordionKey}-${activeAccordionKeys.includes(accordionKey) ? 'open' : 'closed'}`} 
                    className="mb-3"
                    onSelect={(key) => handleAccordionToggle(key, accordionKey)}
                  >
                    <Accordion.Item 
                      eventKey={accordionKey}
                      className="border rounded overflow-hidden"
                    >
                      <Accordion.Header>
                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center w-100 justify-content-between pe-3 gap-2">
                          <div className="d-flex flex-column flex-grow-1">
                            <span className="fw-semibold mb-1">{status.label}</span>
                            <small className="text-muted">
                              Effective: {formatDate((schedule.effectiveDate as unknown as string))}
                            </small>
                          </div>
                          <span className={`badge ${status.badgeClass} px-3 py-2 align-self-start align-self-sm-center`}>
                            {status.badgeText}
                          </span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="p-0">
                        <Schedule className="p-4" schedule={schedule} />
                        <div className={`${style.accordionFooter} d-flex justify-content-between`}>
                          <div
                            className={`${style.accordionItem}`}
                            onClick={() => {
                              scheduleEditItemState.setter(schedule);
                              showEditState.setter(true);
                            }}
                          >
                            <Icon iconName={IconDefinitions.edit} />
                            <span className="ms-1">Edit</span>
                          </div>

                          <div
                            className={`${style.accordionItem}`}
                            onClick={() => {
                              scheduleCloneItemState.setter(schedule);
                              showCloneState.setter(true);
                            }}
                          >
                            <Icon iconName={IconDefinitions.gear}/>
                            <span className="ms-1">Clone</span>
                          </div>

                          <div
                            className={`${style.accordionItem}`}
                            onClick={() => {
                              deleteItemState.setter(schedule);
                              showDeleteState.setter(true);
                            }}
                          >
                            <Icon iconName={IconDefinitions.delete} />
                            <span className="ms-1">Delete</span>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                );
              })}
            </div>
          </Col>
          <Col lg={12} className="border-lg-1">
            <SectionHeader
              title="Schedule Exceptions"
              description="Temporary availability overrides"
              iconName={IconDefinitions.calendar}
              iconBgColor="#edd9cc"
              borderColor="#edd9cc"
              count={exceptionSchedules?.length || 0}
              countLabel="exception"
            />
            <div className="d-flex flex-column justify-content-between">
              {exceptionSchedules && exceptionSchedules.map((schedule, index) => {
                const status = getExceptionStatus(schedule.effectiveDate, schedule.effectiveEndDate);
                const accordionKey = `exception-${schedule.id}`;
               
                return (
                  <Accordion 
                    id={accordionKey} 
                    defaultActiveKey={
                      activeAccordionKeys.includes(accordionKey) || (shouldAutoOpenExcepton && !activeAccordionKeys.length)
                        ? accordionKey 
                        : undefined
                    }
                    key={`${accordionKey}-${activeAccordionKeys.includes(accordionKey) ? 'open' : 'closed'}`}
                    className="mb-3"
                    onSelect={(key) => handleAccordionToggle(key, accordionKey)}
                  >
                    <Accordion.Item 
                      eventKey={accordionKey}
                      className="border rounded overflow-hidden"
                    >
                      <Accordion.Header>
                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center w-100 justify-content-between pe-3 gap-2">
                          <div className="d-flex flex-column flex-grow-1">
                            <span className="fw-semibold mb-1">
                              {formatDate((schedule.effectiveDate as unknown as string))} to {schedule.effectiveEndDate && formatDate((schedule.effectiveEndDate as unknown as string))}
                            </span>
                            <small className="text-muted">
                              Schedule override period
                            </small>
                          </div>
                          <span className={`badge ${status.badgeClass} px-3 py-2 align-self-start align-self-sm-center`}>
                            {status.badgeText}
                          </span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="p-0">
                        <Schedule className="p-4" schedule={schedule} />
                        <div className={`${style.accordionFooter} d-flex justify-content-between`}>
                          <div className={`${style.accordionItem}`} onClick={() => {
                              scheduleEditItemState.setter(schedule);
                              showEditState.setter(true);
                            }}>
                            <Icon iconName={IconDefinitions.edit} />
                            <span className="ms-1">Edit</span>
                          </div>
                          <div className={`${style.accordionItem}`} onClick={() => {
                              deleteItemState.setter(schedule);
                              showDeleteState.setter(true);
                            }}>
                            <Icon iconName={IconDefinitions.delete} />
                            <span className="ms-1">Delete</span>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                );
              })}
            </div>
          </Col>
        </Row>
        <EditScheduleModal
          showState={showEditState}
          schedule={scheduleEditItemState}
          onSubmit={async (scheduleBlocks: ScheduleBlock[], scheduleId: number) => {
            return await updateSchedule(
              scheduleBlocks, 
              scheduleId, 
              scheduleEditItemState.value.scheduleType
            );
          }}
        />
        <AddScheduleModal
          showState={showAddState}
          providerId={parseInt(providerId)}
          scheduleType={scheduleTypeState.value}
          onSubmit={createSchedule}
          label={scheduleTypeState.value === ScheduleType.Default ? "Schedule" : "Exception"}
        />
        <CloneScheduleModal
          showState={showCloneState}
          scheduleId={scheduleCloneItemState.value.id}
          onSubmit={clone}
        />
        <ConfirmationModal
          showState={showDeleteState}
          title={`Delete`}
            message={
            defaultSchedules && defaultSchedules.length === 1 && deleteItemState.value.scheduleType === ScheduleType.Default
                ? `This is the only visible schedule for this provider. Deleting it may activate a previous schedule if one exists, or make this provider unavailable for bookings. Are you sure?`
                : defaultSchedules && defaultSchedules.length > 0 && deleteItemState.value.id === defaultSchedules[0]?.id
                  ? `You are about to delete the current schedule. If a previous schedule exists, it will become active. Otherwise, this provider will have no availability. Are you sure?`
                  : `You are about to delete this schedule, it cannot be undone. Are you sure you want to delete?`
            }
          confirmText="Delete"
          onConfirm={() => deleteSchedule(deleteItemState.value.id)}
        />
      </Card>
    </>
  );
}

export default ProviderSchedulePage;


/** Helper functions to determine style based on the dates **/

const getScheduleStatus = (effectiveDate: any) => {
  const scheduleDate = parseLocalDate(effectiveDate);
  const today = getTodayAtMidnight();
  
  const scheduleTimestamp = scheduleDate.getTime();
  const todayTimestamp = today.getTime();
  
  const isToday = scheduleTimestamp === todayTimestamp;
  const isPast = scheduleTimestamp < todayTimestamp;
  
  const effectiveDateStr = effectiveDate as unknown as string;
  
  if (isToday) {
    return {
      label: "Active (Starting Today)",
      badgeClass: "bg-success",
      badgeText: "Active",
      isActive: true
    };
  } else if (isPast) {
    return {
      label: `Active (Since ${formatDate(effectiveDateStr)})`,
      badgeClass: "bg-success",
      badgeText: "Active",
      isActive: true
    };
  } else {
    return {
      label: `Upcoming (Starts ${formatDate(effectiveDateStr)})`,
      badgeClass: "badge-secondary",
      badgeText: "Upcoming",
      isActive: false
    };
  }
};

const getExceptionStatus = (effectiveDate: any, effectiveEndDate: any) => {
  const startDate = parseLocalDate(effectiveDate);
  const endDate = effectiveEndDate ? parseLocalDate(effectiveEndDate) : null;
  const today = getTodayAtMidnight();
  
  const isActive = startDate <= today && (!endDate || endDate >= today);
  const isUpcoming = startDate > today;
  
  if (isActive) {
    return { badgeClass: "bg-success", badgeText: "Active"};
  } else if (isUpcoming) {
    return { badgeClass: "badge-secondary", badgeText: "Upcoming"};
  } else {
    return { badgeClass: "badge-gray", badgeText: "Ended" };
  }
};

